import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Menu,
  Dropdown,
  Space,
  Tag,
  Select,
  Input,
  Popover,
  Form,
  Switch,
  Divider,
} from "antd";
import { PlusOutlined, FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { CHURN_STATUS_META } from "../shared/orgChurn";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import { useIsMobile } from "../../hooks/useIsMobile";

const { Search } = Input;

// Voci del filtro rapido per tipologia churn: colore del pallino e testo per il
// messaggio (le label visibili vengono da CHURN_STATUS_META, single source).
const CHURN_TYPE_FILTERS = [
  { status: "churn_risk", dot: "#f5222d", label: "a rischio churn" },
  { status: "login_inactive", dot: "#faad14", label: "con login inattivo" },
  {
    status: "creation_inactive",
    dot: "#fa8c16",
    label: "con operatività ferma",
  },
];

const churnDotStyle = (color) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
  display: "inline-block",
  flexShrink: 0,
});

/**
 * FilterForm component that handles the form for adding new filters
 */
const FilterForm = ({ form, filterableColumns, onFinish }) => {
  const [columnSelected, setColumnSelected] = useState(false);

  // Reset columnSelected when form is reset
  useEffect(() => {
    const subscription = form.getFieldInstance('column')?.props?.onChange;
    if (subscription) {
      const originalOnChange = subscription;
      form.getFieldInstance('column').props.onChange = (value) => {
        setColumnSelected(!!value);
        originalOnChange(value);
      };
    }
    return () => {
      if (subscription) {
        form.getFieldInstance('column').props.onChange = subscription;
      }
    };
  }, [form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ width: 300 }}
      initialValues={{
        logicalOperator: "AND",
        operator: "equalTo",
      }}
    >
      <Form.Item
        name="column"
        label="Filtra per colonna"
        rules={[{ required: true, message: "Seleziona una colonna" }]}
      >
        <Select
          placeholder="Seleziona colonna"
          onChange={(value) => {
            setColumnSelected(!!value);
            // Reset operator and value when column changes
            form.setFieldsValue({ operator: "equalTo", value: undefined, specialValue: undefined });
          }}
        >
          {filterableColumns.map((column) => (
            <Select.Option key={column.key} value={column.key}>
              {column.title}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {columnSelected && (
        <>
          <Form.Item
            name="operator"
            label="Operatore"
            rules={[{ required: true, message: "Please select an operator" }]}
          >
            <Select
              placeholder="Seleziona operatore"
              onChange={(value) => {
                // If exists operator is selected, set value to true by default
                if (value === "exists") {
                  form.setFieldsValue({ value: true, specialValue: undefined });
                } else if (value === "isNull" || value === "isNotNull") {
                  // For null operators, clear the value
                  form.setFieldsValue({ value: null, specialValue: undefined });
                }
              }}
            >
              <Select.Option value="equalTo">Uguale a</Select.Option>
              <Select.Option value="notEqualTo">Non uguale a</Select.Option>
              <Select.Option value="greaterThan">Maggiore di</Select.Option>
              <Select.Option value="lessThan">Minore di</Select.Option>
              <Select.Option value="greaterThanOrEqualTo">
                Maggiore o uguale a
              </Select.Option>
              <Select.Option value="lessThanOrEqualTo">
                Minore o uguale a
              </Select.Option>
              <Select.Option value="contains">Contiene</Select.Option>
              <Select.Option value="exists">Esiste</Select.Option>
              <Select.Option value="isNull">È nullo</Select.Option>
              <Select.Option value="isNotNull">Non è nullo</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="logicalOperator"
            label="Operatore logico"
            rules={[
              { required: true, message: "Seleziona un operatore logico" },
            ]}
          >
            <Select>
              <Select.Option value="AND">AND</Select.Option>
              <Select.Option value="OR">OR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Valore" dependencies={["column", "operator", "specialValue"]}>
            {({ getFieldValue }) => {
              const columnKey = getFieldValue("column");
              const operator = getFieldValue("operator");
              const selectedColumn = filterableColumns.find(
                (c) => c.key === columnKey
              );
              const specialValue = getFieldValue("specialValue");
              const useSpecial = specialValue === "__NULL__" || specialValue === "__EMPTY__";
              const isStringColumn = selectedColumn?.type === "string" || (!selectedColumn?.type && selectedColumn?.options === undefined);

              // For exists operator, show a boolean switch
              if (operator === "exists") {
                return (
                  <Form.Item name="value" valuePropName="checked" noStyle>
                    <Switch
                      checkedChildren="Si"
                      unCheckedChildren="No"
                      defaultChecked={true}
                    />
                  </Form.Item>
                );
              }

              // For null checks, no value input is required
              if (operator === "isNull" || operator === "isNotNull") {
                return null;
              }

              // Special value selector (applies to all non-boolean columns)
              if (selectedColumn?.type !== "boolean") {
                return (
                  <>
                    <Form.Item name="specialValue" style={{ marginBottom: 8 }}>
                      <Select placeholder="Valore speciale (opzionale)" allowClear>
                        <Select.Option value="__NULL__">NULL</Select.Option>
                        {isStringColumn && (
                          <Select.Option value="__EMPTY__">Stringa vuota</Select.Option>
                        )}
                      </Select>
                    </Form.Item>

                    {!useSpecial && (
                      selectedColumn?.options ? (
                        <Form.Item
                          name="value"
                          rules={[{
                            validator: (_, v) => {
                              const op = getFieldValue("operator");
                              const spec = getFieldValue("specialValue");
                              if (op === "exists" || op === "isNull" || op === "isNotNull" || spec === "__NULL__" || spec === "__EMPTY__") {
                                return Promise.resolve();
                              }
                              if (v === undefined || v === null) {
                                return Promise.reject("Inserisci un valore");
                              }
                              return Promise.resolve();
                            }
                          }]}
                          noStyle
                        >
                          <Select placeholder="Seleziona valore">
                            {selectedColumn.options.map((option) => (
                              <Select.Option
                                key={String(option.value)}
                                value={option.value}
                              >
                                {option.text}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="value"
                          rules={[{
                            validator: (_, v) => {
                              const op = getFieldValue("operator");
                              const spec = getFieldValue("specialValue");
                              if (op === "exists" || op === "isNull" || op === "isNotNull" || spec === "__NULL__" || spec === "__EMPTY__") {
                                return Promise.resolve();
                              }
                              if (v === undefined || v === null) {
                                return Promise.reject("Inserisci un valore");
                              }
                              return Promise.resolve();
                            }
                          }]}
                          noStyle
                        >
                          <Input placeholder="Inserisci valore" />
                        </Form.Item>
                      )
                    )}
                  </>
                );
              }

              if (selectedColumn?.type === "boolean") {
                return (
                  <>
                    <Form.Item name="specialValue" style={{ marginBottom: 8 }}>
                      <Select placeholder="Valore speciale (opzionale)" allowClear>
                        <Select.Option value="__NULL__">NULL</Select.Option>
                      </Select>
                    </Form.Item>
                    {!useSpecial && (
                      <Form.Item name="value" valuePropName="checked" noStyle>
                        <Switch checkedChildren="Si" unCheckedChildren="No" />
                      </Form.Item>
                    )}
                  </>
                );
              }

              // Fallback - shouldn't reach here
              return null;
            }}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Applica filtro
            </Button>
          </Form.Item>
        </>
      )}
    </Form>
  );
};

/**
 * Dedicated module filter section shown below the main FilterForm
 */
const ModuleFilterSection = ({ modules, onAddModuleFilter }) => {
  const [moduleForm] = Form.useForm();

  return (
    <Form
      form={moduleForm}
      layout="vertical"
      style={{ width: 300 }}
      initialValues={{ moduleOperator: "hasModule" }}
      onFinish={(values) => {
        const mod = modules.find(m => m.objectId === values.moduleId);
        onAddModuleFilter({
          operator: values.moduleOperator,
          moduleId: values.moduleId,
          moduleName: mod?.name || values.moduleId,
        });
        moduleForm.resetFields();
      }}
    >
      <Form.Item
        name="moduleOperator"
        label="Operatore"
      >
        <Select>
          <Select.Option value="hasModule">Ha il modulo</Select.Option>
          <Select.Option value="notHasModule">Non ha il modulo</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="moduleId"
        label="Modulo"
        rules={[{ required: true, message: "Seleziona un modulo" }]}
      >
        <Select
          placeholder="Cerca modulo..."
          showSearch
          optionFilterProp="children"
        >
          {modules.map((m) => (
            <Select.Option key={m.objectId} value={m.objectId}>
              {m.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit">
          Applica filtro modulo
        </Button>
      </Form.Item>
    </Form>
  );
};

/**
 * Enhanced Filters component for the OrgsV2 component
 * Supports multiple column filters, search, and combined filtering
 * Now works with combined search and filter functionality
 * Added support for choosing logical operations (AND, OR) for each filter independently
 * Supports deep linking through URL parameters
 */
const FiltersComponent = ({
  resetFilters,
  onFilterChange,
  activeFilters = [],
  showTagsInline = true,
  searchValue = "",
  onSearchChange,
  onSearch,
  onClear,
  modules = [],
  onChurnFilter,
  churnFilterLoading = false,
  churnCounts = {},
}) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const isMobile = useIsMobile();

  // Parse filters from URL on mount
  useEffect(() => {
    const params = queryString.parse(location.search);
    if (params.filters) {
      try {
        const parsedFilters = JSON.parse(params.filters);
        if (Array.isArray(parsedFilters) && parsedFilters.length > 0) {
          onFilterChange(parsedFilters, true); // Skip query on mount - OrgsV2 handles initial fetch
        }
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on component mount

  // Update URL when filters change
  useEffect(() => {
    // Skip URL update if it's coming from a filter change
    const currentParams = queryString.parse(location.search);
    const newParams = {
      ...currentParams,
    };

    // Only add filters param if there are active filters
    if (activeFilters.length > 0) {
      // Don't use encodeURIComponent here - queryString.stringify will handle encoding
      newParams.filters = JSON.stringify(activeFilters);
    } else {
      // Remove filters param when empty
      delete newParams.filters;
    }

    // IMPORTANT: Preserve page and pageSize parameters
    // Don't remove them even if they're undefined
    if (currentParams.page) {
      newParams.page = currentParams.page;
    }
    if (currentParams.pageSize) {
      newParams.pageSize = currentParams.pageSize;
    }

    // Remove undefined values (except page and pageSize which we want to preserve)
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === undefined && key !== 'page' && key !== 'pageSize') {
        delete newParams[key];
      }
    });

    const newSearch = queryString.stringify(newParams);
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    // Update URL without adding to browser history and without triggering a re-render
    history.replace(newUrl, { skipQuery: true });
  }, [activeFilters, location.pathname, history, location.search]);

  // Quick filters menu
  const quickFiltersMenu = (
    <Menu>
      {/* Clienti Paganti Section */}
      <Menu.ItemGroup title="ORG ATTIVE">
        <Menu.Item
          key="active-clients"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "tipo_istanza",
                columnName: "tipo_istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive
        </Menu.Item>
        <Menu.Item
          key="stripe-customers"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "stripe_customer_id",
                columnName: "Stripe Customer ID",
                value: true,
                displayValue: "Definito",
                operator: "exists",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 2,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive <strong>con</strong> abbonamento Stripe
        </Menu.Item>
        <Menu.Item
          key="active-without-stripe"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 2,
                column: "stripe_customer_id",
                columnName: "Stripe Customer ID",
                value: false,
                displayValue: "false",
                operator: "exists",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive <strong>senza</strong> abbonamento Stripe
        </Menu.Item>
        <Menu.Item
          key="new-clients"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation

            // Calculate date 45 days ago
            const date45DaysAgo = new Date();
            date45DaysAgo.setDate(date45DaysAgo.getDate() - 45);
            const isoDate = date45DaysAgo.toISOString();

            onFilterChange([
              {
                id: Date.now(),
                column: "createdAt",
                columnName: "Data Creazione Org",
                value: isoDate,
                displayValue: "ultimi 45 giorni",
                operator: "greaterThanOrEqualTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 2,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive (<strong>ultimi 45 giorni</strong>)
        </Menu.Item>
        <Menu.Item
          key="e-invoicing"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "fatturazione_attiva",
                columnName: "Fatturazione Elettronica",
                value: true,
                displayValue: "Si",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 2,
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive con <strong>Fatturazione Elettronica</strong>
        </Menu.Item>
        <Menu.Item
          key="obituaries"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "integrazione_annunci_funebri",
                columnName: "Annunci Funebri",
                value: true,
                displayValue: "Si",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 2,
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "production",
                displayValue: "production",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Org attive con <strong>Annunci Funebri</strong>
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup title="ALTRO">
        <Menu.Item
          key="demo-instances"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "demo",
                displayValue: "demo",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Istanze Demo attive
        </Menu.Item>
        <Menu.Item
          key="free-instances"
          onClick={() => {
            // Instead of resetting filters and then adding new ones,
            // directly set the new filters in a single operation
            onFilterChange([
              {
                id: Date.now(),
                column: "tipo_istanza",
                columnName: "Istanza",
                value: "free",
                displayValue: "free",
                operator: "equalTo",
                logicalOperator: "AND",
              },
              {
                id: Date.now() + 1,
                column: "stato",
                columnName: "Stato",
                value: "attiva",
                displayValue: "attiva",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Istanze Free attive
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup title="RICERCHE FREQUENTI">
        <Menu.Item
          key="org-demo-ofc"
          onClick={() => {
            // Create a mock event object with target.value property
            const mockEvent = {
              target: {
                value: "ONORANZE FUNEBRI OFC",
              },
            };

            // Set the search value to "ONORANZE FUNEBRI OFC" using the mock event
            if (onSearchChange) {
              onSearchChange(mockEvent);
            }

            // If there's a search function, trigger it
            if (onSearch) {
              onSearch("ONORANZE FUNEBRI OFC");
            }
          }}
        >
          Org Demo OFC
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup title="MODULI">
        <Menu.Item
          key="module-giottino"
          onClick={() => {
            onFilterChange([
              {
                id: Date.now(),
                column: "__module__",
                columnName: "Modulo",
                value: "lfy97vngsh",
                displayValue: "Giottino",
                operator: "hasModule",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Giottino
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup title="FUNERALCLOUD">
        <Menu.Item
          key="funeralcloud-suppliers"
          onClick={() => {
            onFilterChange([
              {
                id: Date.now(),
                column: "marketplace",
                columnName: "marketplace",
                value: true,
                displayValue: "Si",
                operator: "equalTo",
                logicalOperator: "AND",
              },
            ]);
          }}
        >
          Fornitori
        </Menu.Item>
      </Menu.ItemGroup>

      {onChurnFilter && (
        <Menu.ItemGroup title="CHURN">
          {CHURN_TYPE_FILTERS.map(({ status, dot, label }) => {
            const count = churnCounts[status];
            return (
              <Menu.Item
                key={`churn-${status}`}
                disabled={churnFilterLoading}
                onClick={() => onChurnFilter([status], label)}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={churnDotStyle(dot)} />
                  <span>{CHURN_STATUS_META[status].label}</span>
                  {count != null && (
                    <Badge
                      count={count}
                      showZero
                      overflowCount={9999}
                      style={{
                        backgroundColor: count > 0 ? dot : "#d9d9d9",
                      }}
                    />
                  )}
                </span>
              </Menu.Item>
            );
          })}
        </Menu.ItemGroup>
      )}

      <Menu.Divider />
      <Menu.Item
        key="reset"
        onClick={() => {
          resetFilters();
          // Clear filters from URL
          const currentParams = queryString.parse(location.search);
          delete currentParams.filters;
          const newSearch = queryString.stringify(currentParams);
          history.replace(
            `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
          );
        }}
        danger
      >
        Reset Filters
      </Menu.Item>
    </Menu>
  );

  // Use a static curated configuration instead of dynamic calculation
  // This ensures names of columns and keys match correctly
  const filterableColumns = [
    {
      title: "nome",
      key: "nome",
      dataIndex: "nome",
      type: "string",
    },
    {
      title: "tipo_istanza",
      key: "tipo_istanza",
      dataIndex: "tipo_istanza",
      options: [
        { text: "free", value: "free" },
        { text: "demo", value: "demo" },
        { text: "production", value: "production" },
      ],
    },
    {
      title: "tipo_licenza",
      key: "tipo_licenza",
      dataIndex: "tipo_licenza",
      options: [
        { text: "basic", value: "basic" },
        { text: "enterprise", value: "enterprise" },
        { text: "giotto", value: "giotto" },
        { text: "module", value: "module" },
        { text: "partner", value: "partner" },
        { text: "plus", value: "plus" },
        { text: "pro", value: "pro" },
      ],
    },
    {
      title: "stato",
      key: "stato",
      dataIndex: "stato",
      options: [
        { text: "attiva", value: "attiva" },
        { text: "disattiva", value: "disattiva" },
      ],
    },
    {
      title: "plan",
      key: "plan",
      dataIndex: "plan",
      options: [
        { text: "Basic", value: "Basic" },
        { text: "ENTRY-ANNUALE", value: "ENTRY-ANNUALE" },
        { text: "ENTRY-FLAT-ANNUALE", value: "ENTRY-FLAT-ANNUALE" },
        { text: "ENTRY-LIGHT-ANNUALE", value: "ENTRY-LIGHT-ANNUALE" },
        { text: "ENTRY-LIGHT-MENSILE", value: "ENTRY-LIGHT-MENSILE" },
        { text: "ENTRY-MENSILE", value: "ENTRY-MENSILE" },
        { text: "Eterno Basic Annuale", value: "Eterno Basic Annuale" },
        { text: "Eterno Basic Mensile", value: "Eterno Basic Mensile" },
        { text: "Eterno Entry Annuale", value: "Eterno Entry Annuale" },
        { text: "Eterno Full", value: "Eterno Full" },
        { text: "Eterno Full Annuale", value: "Eterno Full Annuale" },
        { text: "Eterno Full Mensile", value: "Eterno Full Mensile" },
        { text: "Eterno Pro Annuale", value: "Eterno Pro Annuale" },
        { text: "Eterno Pro Mensile", value: "Eterno Pro Mensile" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Annuali - Eterno Full", value: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Annuali - Eterno Full" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Annuali - Eterno Pro", value: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Annuali - Eterno Pro" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Basic", value: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Basic" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Full", value: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Full" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Pro", value: "Eterno-Senza-Licenza-Solo-Servizi-Quota Servizi Mensili - Eterno Pro" },
        { text: "Eterno-Senza-Licenza-Solo-Servizi-Servizi Annunci Funebri Mensili", value: "Eterno-Senza-Licenza-Solo-Servizi-Servizi Annunci Funebri Mensili" },
        { text: "FULL-ANNUALE", value: "FULL-ANNUALE" },
        { text: "FULL-FLAT-ANNUALE", value: "FULL-FLAT-ANNUALE" },
        { text: "FULL-FLAT-MENSILE", value: "FULL-FLAT-MENSILE" },
        { text: "FULL-LIGHT-ANNUALE", value: "FULL-LIGHT-ANNUALE" },
        { text: "FULL-LIGHT-MENSILE", value: "FULL-LIGHT-MENSILE" },
        { text: "GIOTTO-ANNUALE", value: "GIOTTO-ANNUALE" },
        { text: "GIOTTO-MENSILE", value: "GIOTTO-MENSILE" },
        { text: "Giotto Annuale", value: "Giotto Annuale" },
        { text: "Giotto Mensile", value: "Giotto Mensile" },
        { text: "LICENZA PRO - Trimestrale", value: "LICENZA PRO - Trimestrale" },
        { text: "PIANO ENTRY MENSILE", value: "PIANO ENTRY MENSILE" },
        { text: "PIANO PRO ANNUALE", value: "PIANO PRO ANNUALE" },
        { text: "PRO-ANNUALE", value: "PRO-ANNUALE" },
        { text: "PRO-LIGHT-ANNUALE", value: "PRO-LIGHT-ANNUALE" },
        { text: "PRO-LIGHT-MENSILE", value: "PRO-LIGHT-MENSILE" },
        { text: "PRO-MENSILE", value: "PRO-MENSILE" },
        { text: "Quota Licenza Eterno Basic Annuale", value: "Quota Licenza Eterno Basic Annuale" },
        { text: "Quota Licenza Eterno Basic Mensile", value: "Quota Licenza Eterno Basic Mensile" },
        { text: "Quota Licenza Eterno Entry Mensile", value: "Quota Licenza Eterno Entry Mensile" },
        { text: "Quota Licenza Eterno Full Mensile", value: "Quota Licenza Eterno Full Mensile" },
        { text: "Quota Licenza Eterno Pro Annuale", value: "Quota Licenza Eterno Pro Annuale" },
        { text: "Quota Licenza Eterno Pro Mensile", value: "Quota Licenza Eterno Pro Mensile" },
      ]
    },
    {
      title: "integrazione_annunci_funebri",
      key: "integrazione_annunci_funebri",
      dataIndex: "integrazione_annunci_funebri",
      type: "boolean",
      options: [
        { text: "Si", value: true },
        { text: "No", value: false },
      ],
    },
    {
      title: "fatturazione_attiva",
      key: "fatturazione_attiva",
      dataIndex: "fatturazione_attiva",
      type: "boolean",
      options: [
        { text: "Si", value: true },
        { text: "No", value: false },
      ],
    },
    {
      title: "stripe_customer_id",
      key: "stripe_customer_id",
      dataIndex: "stripe_customer_id",
      type: "string",
    },
    {
      title: "citta",
      key: "citta",
      dataIndex: "citta",
      type: "string",
    },
    {
      title: "provincia",
      key: "provincia",
      dataIndex: "provincia",
      type: "string",
    },
    {
      title: "cap",
      key: "cap",
      dataIndex: "cap",
      type: "string",
    },
    {
      title: "createdAt",
      key: "createdAt",
      dataIndex: "createdAt",
      type: "date",
    },
  ];

  // Handle adding a module filter from the dedicated section
  const handleAddModuleFilter = ({ operator, moduleId, moduleName }) => {
    const newFilter = {
      id: Date.now(),
      column: "__module__",
      columnName: "Modulo",
      value: moduleId,
      displayValue: moduleName,
      operator: operator,
      logicalOperator: "AND",
    };
    onFilterChange([...activeFilters, newFilter]);
    setVisible(false);
  };

  // Add new filter handler
  const handleAddFilter = (values) => {
    console.log("Form values:", values);

    const selectedColumn = filterableColumns.find(
      (c) => c.key === values.column
    );

    // Compute effective operator/value considering specialValue
    const isExistsOperator = values.operator === "exists";
    let effectiveOperator = values.operator;
    let effectiveValue = values.value;

    if (values.specialValue === "__NULL__") {
      effectiveOperator = values.operator === "notEqualTo" ? "isNotNull" : "isNull";
      effectiveValue = null;
    } else if (values.specialValue === "__EMPTY__") {
      effectiveValue = "";
    }

    const isNullOperator = effectiveOperator === "isNull";
    const isNotNullOperator = effectiveOperator === "isNotNull";

    // Check if we have valid inputs
    const requiresValue = !isExistsOperator && !isNullOperator && !isNotNullOperator && values.specialValue !== "__NULL__";
    if (values.column && (!requiresValue || (effectiveValue !== undefined && effectiveValue !== null))) {
      let displayValue;
      if (isExistsOperator) {
        displayValue = values.value ? "esiste" : "non esiste";
      } else if (isNullOperator) {
        displayValue = "NULL";
      } else if (isNotNullOperator) {
        displayValue = "NOT NULL";
      } else if (values.specialValue === "__EMPTY__") {
        displayValue = '""';
      } else if (selectedColumn?.type === "boolean") {
        displayValue = values.value ? "Si" : "No";
      } else {
        displayValue =
          selectedColumn?.options?.find((opt) => opt.value === values.value)
            ?.text || values.value;
      }

      const newFilter = {
        id: Date.now(),
        column: values.column,
        columnName: selectedColumn?.title || values.column,
        value: isNullOperator || isNotNullOperator ? null : effectiveValue,
        displayValue: displayValue,
        operator: effectiveOperator,
        logicalOperator: values.logicalOperator || "AND",
      };

      console.log("Adding new filter:", newFilter);

      // Don't skip the query here - this is the final action that should trigger a query
      onFilterChange([...activeFilters, newFilter]);
      form.resetFields();
      setVisible(false);
    }
  };

  // Remove filter handler
  const handleRemoveFilter = (filterId) => {
    onFilterChange(activeFilters.filter((f) => f.id !== filterId));
  };

  // Get tag color based on logical operator
  const getTagColor = (logicalOperator) => {
    switch (logicalOperator) {
      case "OR":
        return "orange";
      case "AND":
      default:
        return "blue";
    }
  };

  const getOperatorDisplay = (operator) => {
    switch (operator) {
      case "equalTo":
        return "=";
      case "notEqualTo":
        return "≠";
      case "greaterThan":
        return ">";
      case "greaterThanOrEqualTo":
        return "≥";
      case "lessThan":
        return "<";
      case "lessThanOrEqualTo":
        return "≤";
      case "contains":
        return "contiene";
      case "in":
        return "in";
      case "notIn":
        return "not in";
      case "exists":
        return "exists";
      case "isNull":
        return "is NULL";
      case "isNotNull":
        return "is NOT NULL";
      case "hasModule":
        return "ha il modulo";
      case "notHasModule":
        return "non ha il modulo";
      default:
        return operator;
    }
  };

  const formatFilterTag = (filter) => {
    const op = filter.operator || "equalTo";
    const logic = filter.logicalOperator || "AND";
    const name = filter.columnName || filter.column;

    if (op === "hasModule") {
      return `Ha modulo: ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "notHasModule") {
      return `Non ha modulo: ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "isNull") {
      return `${name} IS NULL (${logic})`;
    }
    if (op === "isNotNull") {
      return `${name} IS NOT NULL (${logic})`;
    }
    if (op === "exists") {
      return `${name} Esiste: ${filter.value ? "Si" : "No"} (${logic})`;
    }
    if (op === "contains") {
      return `${name} contiene ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "in" || op === "notIn") {
      return `${name} ${getOperatorDisplay(op)} ${filter.displayValue ?? ""} (${logic})`;
    }
    return `${name} ${getOperatorDisplay(op)} ${filter.displayValue ?? ""} (${logic})`;
  };

  return (
    <div className={isMobile ? "filters-component-mobile" : "filters-component"}>
      {/* Mobile: Search on top, full width */}
      {isMobile && onSearch && (
        <div className="filters-search-mobile">
          <Search
            placeholder="Cerca per nome o id"
            allowClear
            enterButton
            value={searchValue}
            onChange={onSearchChange}
            onSearch={onSearch}
            onClear={onClear}
            style={{ width: "100%" }}
          />
        </div>
      )}

      <Space wrap style={{ marginBottom: showTagsInline ? 8 : 0, width: isMobile ? "100%" : "auto" }}>
        {/* Add filter button with popover form */}
        <Popover
          content={
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Filtra per colonna</div>
              <FilterForm
                form={form}
                filterableColumns={filterableColumns}
                onFinish={handleAddFilter}
              />
              {modules.length > 0 && (
                <>
                  <Divider style={{ margin: "12px 0" }} />
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Filtra per modulo</div>
                  <ModuleFilterSection
                    modules={modules}
                    onAddModuleFilter={handleAddModuleFilter}
                  />
                </>
              )}
            </div>
          }
          trigger="click"
          visible={visible}
          onVisibleChange={setVisible}
          placement={isMobile ? "bottomLeft" : "bottom"}
        >
          <Button icon={<PlusOutlined />}>{isMobile ? "Filtro" : "Nuovo filtro"}</Button>
        </Popover>

        {/* Quick filters dropdown */}
        <Dropdown overlay={quickFiltersMenu}>
          <Button icon={<FilterOutlined />}>{isMobile ? "Veloci" : "Filtri veloci"}</Button>
        </Dropdown>

        {/* Desktop: Search field inline */}
        {!isMobile && onSearch && (
          <Search
            placeholder="Cerca per nome o id"
            allowClear
            enterButton
            value={searchValue}
            onChange={onSearchChange}
            onSearch={onSearch}
            onClear={onClear}
            style={{ width: 400, marginLeft: 16 }}
          />
        )}

        {/* Reset all filters */}
        {activeFilters.length > 0 && (
          <Button
            icon={<CloseOutlined />}
            onClick={() => {
              resetFilters();
              // Clear filters from URL
              const currentParams = queryString.parse(location.search);
              delete currentParams.filters;
              const newSearch = queryString.stringify(currentParams);
              history.replace(
                `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
              );
            }}
            danger
          >
            {isMobile ? "Reset" : "Resetta filtri"}
          </Button>
        )}
      </Space>

      {/* Display active filters as tags */}
      {activeFilters.length > 0 && showTagsInline && (
        <Space wrap>
          {activeFilters.map((filter) => (
            <Tag
              key={filter.id}
              closable
              onClose={() => handleRemoveFilter(filter.id)}
              color={getTagColor(filter.logicalOperator)}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Space>{formatFilterTag(filter)}</Space>
            </Tag>
          ))}
        </Space>
      )}
    </div>
  );
};

// Export a separate component for filter tags
export const FilterTags = ({ activeFilters = [], onRemoveFilter }) => {
  // Get tag color based on logical operator
  const getTagColor = (logicalOperator) => {
    switch (logicalOperator) {
      case "OR":
        return "orange";
      case "AND":
      default:
        return "blue";
    }
  };

  const getOperatorDisplay = (operator) => {
    switch (operator) {
      case "equalTo":
        return "=";
      case "notEqualTo":
        return "≠";
      case "greaterThan":
        return ">";
      case "greaterThanOrEqualTo":
        return "≥";
      case "lessThan":
        return "<";
      case "lessThanOrEqualTo":
        return "≤";
      case "in":
        return "in";
      case "notIn":
        return "not in";
      case "exists":
        return "exists";
      case "isNull":
        return "IS NULL";
      case "isNotNull":
        return "IS NOT NULL";
      case "hasModule":
        return "ha il modulo";
      case "notHasModule":
        return "non ha il modulo";
      default:
        return operator;
    }
  };

  const formatFilterTag = (filter) => {
    const op = filter.operator || "equalTo";
    const logic = filter.logicalOperator || "AND";
    const name = filter.columnName || filter.column;

    if (op === "hasModule") {
      return `Ha modulo: ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "notHasModule") {
      return `Non ha modulo: ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "isNull") {
      return `${name} IS NULL (${logic})`;
    }
    if (op === "isNotNull") {
      return `${name} IS NOT NULL (${logic})`;
    }
    if (op === "exists") {
      return `${name} Esiste: ${filter.value ? "Si" : "No"} (${logic})`;
    }
    if (op === "contains") {
      return `${name} contiene ${filter.displayValue ?? ""} (${logic})`;
    }
    if (op === "in" || op === "notIn") {
      return `${name} ${getOperatorDisplay(op)} ${filter.displayValue ?? ""} (${logic})`;
    }
    return `${name} ${getOperatorDisplay(op)} ${filter.displayValue ?? ""} (${logic})`;
  };

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="filters-container">
      <div style={{ marginRight: 8, fontWeight: "bold" }}>Filtri attivi:</div>
      <Space wrap style={{ flex: 1 }}>
        {activeFilters.map((filter) => (
          <Tag
            key={filter.id}
            closable
            onClose={() => onRemoveFilter(filter.id)}
            color={getTagColor(filter.logicalOperator)}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Space>{formatFilterTag(filter)}</Space>
          </Tag>
        ))}
      </Space>
    </div>
  );
};

export default FiltersComponent;
